import React from 'react'

import dayjs, { Dayjs } from 'dayjs'
import timezonePlugin from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import humanize from 'humanize-string'

dayjs.extend(utc)
dayjs.extend(timezonePlugin)

const MAX_STRING_LENGTH = 150
const DATETIME_LOCAL_PATTERN =
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?$/

const parseDatetime = (
  datetime: string | Dayjs | Date | null | undefined,
  timezone?: string
): Dayjs => {
  if (
    timezone &&
    typeof datetime === 'string' &&
    DATETIME_LOCAL_PATTERN.test(datetime)
  ) {
    return dayjs.tz(datetime ?? null, timezone)
  }

  const parsed = dayjs(datetime ?? null)
  return timezone ? parsed.tz(timezone) : parsed
}

export const formatEnum = (values: string | string[] | null | undefined) => {
  let output = ''

  if (Array.isArray(values)) {
    const humanizedValues = values.map((value) => humanize(value))
    output = humanizedValues.join(', ')
  } else if (typeof values === 'string') {
    output = humanize(values)
  }

  return output
}

export const jsonDisplay = (obj: unknown) => {
  return (
    <pre>
      <code>{JSON.stringify(obj, null, 2)}</code>
    </pre>
  )
}

export const listDisplay = (values: string[] | null | undefined) => {
  let output: string | React.JSX.Element = ''

  if (Array.isArray(values)) {
    output = (
      <ol>
        {values.map((value) => (
          <li key={value}>{value}</li>
        ))}
      </ol>
    )
  }

  return output
}

export const truncate = (value: string | number) => {
  let output = value?.toString() ?? ''

  if (output.length > MAX_STRING_LENGTH) {
    output = output.substring(0, MAX_STRING_LENGTH) + '...'
  }

  return output
}

export const jsonTruncate = (obj: unknown) => {
  return truncate(JSON.stringify(obj, null, 2))
}

export const timeTag = (dateTime?: string) => {
  let output: string | React.JSX.Element = ''

  if (dateTime) {
    output = (
      <time dateTime={dateTime} title={dateTime}>
        {new Date(dateTime).toUTCString()}
      </time>
    )
  }

  return output
}

export const checkboxInputTag = (checked: boolean) => {
  return <input type="checkbox" checked={checked} disabled />
}

/**
 * Formats a datetime for display.
 */
export const formatDatetime = ({
  datetime,
  fallback,
  template = 'ddd, MMM D, YYYY h:mm A',
  timezone,
}: {
  datetime: string | Dayjs | Date | null | undefined
  fallback?: string
  template?: string
  timezone?: string
}): string => {
  const dt = parseDatetime(datetime, timezone)
  if (fallback && !dt.isValid()) {
    return fallback
  }
  return dt.format(template)
}

/**
 * Formats a datetime for a datetime-local form field.
 */
export const formatDatetimeLocalValue = (
  datetime: string | Dayjs | Date | null | undefined,
  timezone?: string
): string | null => {
  const dt = parseDatetime(datetime, timezone)
  if (!dt.isValid()) {
    return null
  }
  return dt.format('YYYY-MM-DDTHH:mm')
}

/**
 * Formats a date for a date form field.
 */
export const formatDateValue = (
  date: string | Dayjs | Date | null | undefined
): string | null => {
  const dt = dayjs(date)
  if (!dt.isValid()) {
    return null
  }
  return dt.format('YYYY-MM-DD')
}

/**
 * Formats a datetime for GraphQL input.
 */
export const formatGqlDatetime = (
  datetime: string | Dayjs | Date | null | undefined,
  timezone?: string
): string | null => {
  const dt = parseDatetime(datetime, timezone)
  if (!dt.isValid()) {
    return null
  }
  return dt.toISOString()
}
